import { AutoComplete, Input } from 'antd';
import { incorrectEmail } from 'app-constants';
import classNames from 'classnames';
import { Spinner } from 'components';
import { Divider, DraggableCombineMenuList, DraggableCombineMenuListItem } from 'components-antd';
import { SignFormToggleRow } from 'pages/FormProcess/components/PreForm/SignForm/SignFormToggleRow';
import { isValidEmail, sortDetails, sortOptions, validationToast } from 'pages/FormProcess/helper';
import { ICON_VARIANT_TYPE, Icons } from 'pages/FormProcess/Icons';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getFormMetaSelector } from 'store/selectors/requestFormProcess';

import styles from '../../styles.module.scss';
import { getBoxBorderStyle } from 'pages/FormProcess/components/PreForm/SignForm/helper';
import { caseInsensitiveFilter } from 'helpers';
import { FormNotesModal } from '../FormNotesModal';
import { NotesDropdown } from './NotesDropdown';
import { updatePostFormEffect } from 'store/effects/formProcess';

export const FormSignatories = (props) => {
  const {
    loading,
    setLoading,
    signatories,
    noteMessage,
    setNoteMessage,
    setSignatories,
    setSequenceModified,
    isSequentialSigning,
    updateSignatureSequence,
    updatePostFormDetails,
  } = props;

  const { clients = [], agents = [] } = useSelector(getFormMetaSelector);

  const countCorrection = signatories?.length ? signatories[0]?.sequence - 1 : 0;

  const signatoriesList = useMemo(() => [...(agents || []), ...(clients || [])], [clients, agents]);

  const [signatoryOptions, setSignatoryOptions] = useState([] as any);

  const [openNoteModal, setOpenNoteModal] = useState(false);

  useEffect(() => {
    if (signatoriesList.length) {
      const selectedOptions = signatories
        .map((value) => value.Name && (value.UserId || value.userId))
        .filter((item) => item);

      const filteredOptions = signatoriesList.filter((item) => !selectedOptions.includes(item.Id));

      setSignatoryOptions(
        filteredOptions.map((signatory) => ({
          key: signatory.Id,
          label: `${signatory.FirstName} ${signatory.LastName}`,
          value: `${signatory.FirstName} ${signatory.LastName}`,
        })),
      );
    }
  }, [signatoriesList, signatories]);

  const handleOnBlur = (signatoryIndex: number, nameInput = false) => {
    const signatory = signatories?.[signatoryIndex];

    if (signatory) {
      let updatedSignatories = [...signatories];

      if (nameInput) {
        let isCustomName = !!signatory.Name?.trim().length && !signatory.UserId;

        if (isCustomName) {
          updatedSignatories[signatoryIndex].customName = true;
          updatedSignatories[signatoryIndex].isReplaced = !!signatory?.signatoryId;

          if (signatory?.signatoryId) {
            updatedSignatories[signatoryIndex].Email = '';
          }
        }
      } else if (signatory.Email && !isValidEmail(signatory.Email.trim())) {
        updatedSignatories[signatoryIndex].Email = '';
        validationToast(incorrectEmail);
      }

      setSignatories(updatedSignatories);
    }
  };

  const handleSignatoriesSelect = (value: string, index, option) => {
    const userId = option.key;
    const signatory = signatories?.[index];
    const details = signatoriesList.find((sig) => sig.Id === userId);
    if (signatory) {
      const updatedSignatories = [...signatories];
      const signatoryData = {
        ...signatory,
        isReplaced: !!signatory?.signatoryId,
        UserId: userId,
        Name: value,
        Email: details?.Email,
      };

      signatoryData.customName && delete signatoryData.customName;

      updatedSignatories[index] = { ...signatoryData };

      setSignatories(updatedSignatories);
    }
  };

  const handleSignatoriesChange = (
    key: string,
    value: string,
    index: number,
    nameInput = false,
  ) => {
    const signatory = signatories?.[index];

    if (signatory) {
      if (signatory.UserId) {
        const isExist = signatoryOptions.find(({ key }) => key === signatory.UserId);

        if (!isExist) {
          signatoryOptions.push({
            key: signatory.UserId,
            label: signatory.Name as string,
            value: signatory.Name as string,
          });
          setSignatoryOptions(signatoryOptions);
        }
      }

      const updatedSignatories = [...signatories];
      const signatoryData = {
        ...signatory,
        [key]: value,
      };

      signatoryData.UserId && delete signatoryData.Email;
      delete signatoryData.UserId;

      if (nameInput && !value?.trim().length) {
        delete signatoryData.customName;
        delete signatoryData.Email;
      }

      updatedSignatories[index] = { ...signatoryData };

      setSignatories(updatedSignatories);
    }
  };

  const renderSignatory = (item: DraggableCombineMenuListItem) => {
    const signatoryIndex = signatories?.findIndex(
      (signatory) => signatory.signatoryId === item?.id,
    );
    const signatory = signatories?.[signatoryIndex];

    if (!signatory) {
      return <></>;
    }

    const getNumberTag = () => {
      if (!isSequentialSigning) return;
      return <div className={styles.numberTag}>{item.sequence}</div>;
    };

    return (
      <div className={classNames(styles.signFieldBox)} key={`signatory-${signatory.signatoryId}`}>
        {getNumberTag()}

        <div className={styles.fieldWrapper} style={getBoxBorderStyle(signatoryIndex)}>
          <div
            className={classNames(styles.field, {
              [styles.signFormField]: true,
            })}
          >
            <div className={styles.fieldHeader}>
              <div className={styles.label}>{signatory.Role || 'Recipient'}</div>

              <div className={styles.noteActions}>
                {signatory.note && <Icons variant={ICON_VARIANT_TYPE.NotesWithMessage} />}
                <NotesDropdown
                  signatory={signatory}
                  onUpdateNote={(note) => {
                    const copiedSignatories = [...signatories];
                    copiedSignatories[signatoryIndex].note = note;
                    setSignatories(copiedSignatories);
                  }}
                />
              </div>
            </div>

            <div className={styles.fieldsContainer}>
              <div className={styles.field}>
                <AutoComplete
                  placeholder="Enter name"
                  bordered={false}
                  className={styles.autoCompleteInput}
                  value={signatory.Name}
                  options={sortOptions(signatoryOptions, 'label')}
                  filterOption={(inputValue, option) =>
                    caseInsensitiveFilter(inputValue, option?.label as string)
                  }
                  onBlur={() => handleOnBlur(signatoryIndex, true)}
                  onSelect={(value, option) =>
                    handleSignatoriesSelect(value, signatoryIndex, option)
                  }
                  onChange={(value) => handleSignatoriesChange('Name', value, signatoryIndex, true)}
                  allowClear
                  getPopupContainer={(node) => node}
                  popupClassName={styles.autoCompleteDropdown}
                />
              </div>

              <div className={styles.field}>
                <Input
                  className={styles.email}
                  placeholder={'Enter email address'}
                  value={signatory.Email}
                  disabled={!!signatory.UserId}
                  onChange={({ target }) =>
                    !signatory.UserId
                      ? handleSignatoriesChange('Email', target.value, signatoryIndex)
                      : null
                  }
                  onBlur={() => handleOnBlur(signatoryIndex)}
                  title={signatory.UserId ? signatory.Email : ''}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getInitialItems = () =>
    signatories.map((signer) => ({
      id: signer.signatoryId,
      sequence: signer.sequence - countCorrection,
    })) as DraggableCombineMenuListItem[];

  /** Sequence Related */
  const signatoryToggleChange = () => {
    const isSequence = !isSequentialSigning;
    const updatedSignatories = signatories.map((s, index) => ({
      ...s,
      sequence: isSequence ? index + 1 : 1,
    }));

    setSequenceModified(true);
    updateSignatureSequence(updatedSignatories, isSequence);
  };

  const onSequenceUpdate = (updatedSigners) => {
    const groupedByCorrection = updatedSigners.reduce((acc, signer) => {
      acc[signer.id] = { ...signer, sequence: signer.sequence + countCorrection };
      return acc;
    }, {});

    const copiedSignatories = [...signatories];

    copiedSignatories.forEach((item) => {
      const updatedSequence = groupedByCorrection[item.signatoryId]?.sequence;
      item.sequence = updatedSequence;
      item.sequenceUpdated = updatedSequence !== item.sequence;
    });

    const sortedSignatories = sortDetails(copiedSignatories, 'sequence');
    setSequenceModified(true);
    setSignatories(sortedSignatories);

    updatePostFormDetails({
      sequenceModified: true,
      signatories: sortedSignatories,
    });
  };

  /** Notes Related */
  const toggleNoteModal = () => {
    setOpenNoteModal((prev) => !prev);
  };

  const onSaveNote = (message) => {
    setNoteMessage(message);
    toggleNoteModal();
  };

  return (
    <Fragment>
      <div className={styles.listSequenceWrapper}>
        {loading ? (
          <Spinner />
        ) : (
          <div className={styles.fieldsListSequenceWrapper}>
            <DraggableCombineMenuList
              initialItems={getInitialItems()}
              onItemUpdate={onSequenceUpdate}
              renderFunction={renderSignatory}
              isDisabled={!isSequentialSigning || signatories.length === 1}
              droppableClassName={`${styles.fieldsDroppable}`}
            />
          </div>
        )}

        {signatories.length > 1 && !loading && (
          <div className={styles.postFormSignSequenceWrapper}>
            <SignFormToggleRow
              handleSequentialSigning={signatoryToggleChange}
              isSequentialSignature={isSequentialSigning}
              disabled={loading}
              className={styles.postFormSignSequence}
            />

            <Divider className={styles.formDivider} />

            <div className={styles.noteSection} onClick={toggleNoteModal}>
              <div className={styles.noteWrapper}>
                <Icons variant={ICON_VARIANT_TYPE.Notes} />
                <div className={styles.noteTitle}>{noteMessage ? 'Edit Note' : 'Add a Note'}</div>
              </div>
              {/* <Icons className={styles.noteInfo} variant={ICON_VARIANT_TYPE.Info} /> */}
            </div>
          </div>
        )}
      </div>

      <FormNotesModal
        isOpen={openNoteModal}
        defaultNote={noteMessage}
        onSaveNote={onSaveNote}
        onClose={toggleNoteModal}
        signatories={signatories || []}
      />
    </Fragment>
  );
};
